import { useMutation } from '@tanstack/react-query';
import { stripeService } from '../services/stripeService';
import type { Invoice } from '@clinic-os/shared/types';
import { toast } from 'sonner';

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async ({
      invoice,
      clinicSlug,
    }: {
      invoice: Invoice;
      clinicSlug: string;
    }) => {
      const result = await stripeService.createCheckoutSession(invoice, clinicSlug);
      if (!result.success || !result.checkoutUrl) {
        throw new Error(result.error || 'Failed to create checkout session');
      }
      return result;
    },
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      window.location.href = data.checkoutUrl!;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create checkout session');
    },
  });
}

export function useCreatePaymentLink() {
  return useMutation({
    mutationFn: async ({
      invoice,
      clinicSlug,
    }: {
      invoice: Invoice;
      clinicSlug: string;
    }) => {
      const result = await stripeService.createPaymentLink(invoice, clinicSlug);
      return result;
    },
    onSuccess: (data) => {
      // Copy payment link to clipboard
      navigator.clipboard
        .writeText(data.url)
        .then(() => {
          toast.success('Payment link copied to clipboard!');
        })
        .catch(() => {
          toast.success('Payment link created', {
            description: data.url,
          });
        });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create payment link');
    },
  });
}

export function useVerifyPayment() {
  return useMutation({
    mutationFn: async (paymentIntentId: string) => {
      return stripeService.verifyPayment(paymentIntentId);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to verify payment');
    },
  });
}

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: async ({
      customerId,
      returnUrl,
    }: {
      customerId: string;
      returnUrl: string;
    }) => {
      return stripeService.createPortalSession(customerId, returnUrl);
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create portal session');
    },
  });
}

export function useStripeConfigured() {
  return stripeService.isConfigured();
}
